import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Plus,
  CheckCircle,
  Circle,
  Clock,
  LayoutGrid,
  FolderKanban,
  KanbanSquare,
  ListTodo,
  Bug,
  BarChart3,
  Users,
  Settings,
  X,
} from "lucide-react";


/* ================= HELPER ================= */
const navigate = (path: string) => {
  window.location.hash = path;
};

export default function Backlog() {
  const [sprints, setSprints] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSprint, setNewSprint] = useState({
    name: "",
    goal: "",
    startDate: "",
    endDate: "",
    projectId: 2, // Default project ID for now
  });

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'product_manager' || user?.role === 'scrum_master';

  const fetchSprints = async () => {
    try {
      const response = await fetch("/api/get_sprints.php");
      if (response.ok) {
        const data = await response.json();
        setSprints(data);
      }
    } catch (error) {
      console.error("Failed to fetch sprints", error);
    }
  };

  useEffect(() => {
    fetchSprints();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSprint((prev) => ({ ...prev, [name]: value }));
  };

  const createSprint = async () => {
    if (!newSprint.name || !newSprint.startDate || !newSprint.endDate) {
      return alert("Please fill in all required fields");
    }

    try {
      const response = await fetch("/api/create_sprint.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newSprint),
      });

      const data = await response.json();

      if (data.success) {
        fetchSprints();
        setIsModalOpen(false);
        setNewSprint({
          name: "",
          goal: "",
          startDate: "",
          endDate: "",
          projectId: 2,
        });
      } else {
        alert(data.message || "Failed to create sprint");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to create sprint");
    }
  };

  return (
    <div className="flex flex-1 bg-[#f6f7fb]">

      {/* ================= SIDEBAR ================= */}
      <aside className="w-64 shrink-0 bg-white border-r px-4 py-5">
        {isAdmin && (
          <button
            onClick={() => navigate("issues")}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-md mb-6 text-sm font-medium"
          >
            + Create Issue
          </button>
        )}

        <Menu icon={LayoutGrid} label="Main Dashboard" path="dashboard" />

        {isAdmin && (
          <>
            <Menu icon={LayoutGrid} label="Jira Dashboard" path="jira" />
            <Menu icon={FolderKanban} label="Projects" path="projects" />
          </>
        )}

        <Menu icon={KanbanSquare} label="Board" path="board" />

        {isAdmin && (
          <>
            <Menu icon={ListTodo} label="Backlog" path="backlog" active />
            <Menu icon={Bug} label="Issues" path="issues" />
            <Menu icon={Clock} label="Time Tracking" path="time" />
            <Menu icon={BarChart3} label="Reports" path="reports" />
            <Menu icon={Users} label="Team" path="team" />
            <Menu icon={Settings} label="Settings" path="settings" />
          </>
        )}

        <div className="mt-8 text-xs text-gray-400">
          <p className="mb-2 uppercase">Recent Projects</p>
          <p className="text-green-600">WEB Website Redesign</p>
          <p className="text-green-600">MOB Mobile App</p>
          <p className="text-green-600">CRM CRM Enhancement</p>
        </div>
      </aside>

      {/* ================= MAIN ================= */}
      <main className="flex-1 p-6 overflow-y-auto relative">

        {/* TOP TABS */}
        <div className="flex gap-3 mb-6 text-sm">
          <Tab label="Dashboard" path="jira" />
          <Tab label="Projects" path="projects" />
          <Tab label="Board" path="board" />
          <Tab label="Backlog" path="backlog" active />
          <Tab label="Issues" path="issues" />
        </div>

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-semibold">Backlog</h1>
            <p className="text-sm text-gray-500">
              Plan and prioritize your work
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm"
          >
            <Plus size={16} /> Create Sprint
          </button>
        </div>

        {/* ================= SPRINTS ================= */}

        {sprints.length > 0 ? (
          sprints.map((sprint: any) => (
            <Sprint
              key={sprint.id}
              title={sprint.name}
              status={sprint.status || "planning"}
              subtitle={sprint.goal}
              date={`${sprint.start_date} to ${sprint.end_date}`}
            >
              {/* Placeholder for sprint issues - functionality to add issues to sprint would be next step */}
              <div className="text-xs text-gray-400 italic p-2">No issues in this sprint yet.</div>
            </Sprint>
          ))
        ) : (
          <div className="text-center text-gray-500 py-10">No sprints found. Create one to get started.</div>
        )}

        {/* BACKLOG ISSUES */}
        <div className="mt-6 bg-white rounded-lg border p-4">
          <h3 className="font-semibold mb-3">Backlog Issues</h3>
          <Issue id="WEB-103" title="User authentication system" priority="high" />
        </div>

        {/* ================= CREATE SPRINT MODAL ================= */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg w-96 shadow-lg relative">
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-black"
              >
                <X size={18} />
              </button>
              <h2 className="text-lg font-semibold mb-4">Create New Sprint</h2>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sprint Name</label>
                  <input
                    type="text"
                    name="name"
                    value={newSprint.name}
                    onChange={handleInputChange}
                    className="w-full border rounded p-2 text-sm"
                    placeholder="e.g. Sprint 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Sprint Goal</label>
                  <input
                    type="text"
                    name="goal"
                    value={newSprint.goal}
                    onChange={handleInputChange}
                    className="w-full border rounded p-2 text-sm"
                    placeholder="e.g. Complete User Auth"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={newSprint.startDate}
                    onChange={handleInputChange}
                    className="w-full border rounded p-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={newSprint.endDate}
                    onChange={handleInputChange}
                    className="w-full border rounded p-2 text-sm"
                  />
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 rounded text-sm hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createSprint}
                    className="px-4 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

/* ================= COMPONENTS ================= */

function Menu({ label, path, active, icon: Icon }: any) {
  return (
    <button
      onClick={() => navigate(path)}
      className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition
      ${active
          ? "bg-green-100 text-green-700 font-medium"
          : "text-gray-700 hover:bg-gray-100"
        }`}
    >
      {Icon && <Icon size={16} />}
      <span>{label}</span>
    </button>
  );
}

function Tab({ label, path, active }: any) {
  return (
    <button
      onClick={() => navigate(path)}
      className={`px-3 py-1.5 rounded-md text-sm transition
      ${active
          ? "bg-green-500 text-white"
          : "bg-white border text-gray-700 hover:bg-gray-100"
        }`}
    >
      {label}
    </button>
  );
}

function Sprint({ title, status, subtitle, date, children }: any) {
  const statusStyle: any = {
    completed: "bg-blue-100 text-blue-600",
    active: "bg-green-100 text-green-600",
    planning: "bg-gray-100 text-gray-600",
  };

  const statusIcon: any = {
    completed: <CheckCircle size={14} />,
    active: <Circle size={14} />,
    planning: <Clock size={14} />,
  };

  return (
    <div className="bg-white rounded-lg border mb-2 px-4 py-1.5">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{title}</h3>
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${statusStyle[status] || "bg-gray-100 text-gray-600"}`}>
            {statusIcon[status]}
            {status}
          </span>
        </div>
        <span className="text-xs text-gray-400">{date}</span>
      </div>

      <p className="text-xs text-gray-500 mb-1 leading-tight">{subtitle}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Issue({ id, title, priority }: any) {
  const priorityColor: any = {
    low: "bg-blue-100 text-blue-600",
    medium: "bg-yellow-100 text-yellow-700",
    high: "bg-orange-100 text-orange-700",
  };

  return (
    <div className="flex justify-between items-center border rounded-md px-3 py-1 text-xs hover:bg-gray-50">
      <div>
        <p className="text-xs text-gray-500">{id}</p>
        <p>{title}</p>
      </div>

      <span className={`text-xs px-2 py-0.5 rounded ${priorityColor[priority]}`}>
        {priority}
      </span>
    </div>
  );
}
